import { QRScannerDialog, QRScannerDialogRef } from '@/components/QRScannerDialog'
import { SectionCard } from '@/components/SectionCard'
import { Button } from '@/components/ui/button'
import { MultiSelect, MultiSelectOption } from '@/components/ui/multi-select'
import { PatientCard } from '@/features/patients/components/PatientCard'
import {
  PatientSearchSelector,
  PatientSearchSelectorRef,
} from '@/features/patients/components/search/PatientSearchSelector'
import { useGetPatientItems } from '@/features/patients/hooks/usePatient'
import { Patient } from '@/features/patients/types/patient'
import { useConfirm } from '@/hooks/useConfirm'
import { getMutationOptions } from '@/utils/mutation'
import { showError } from '@/utils/toast'
import { ScanLine } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAssignBed } from '../../hooks/useBed'
import { Bed } from '../../types/bed'

export function PatientToBedSection({
  departmentId,
  bed,
  patient,
}: {
  departmentId: string
  bed: Bed
  patient?: Patient
}) {
  const { t } = useTranslation()
  const confirm = useConfirm()

  const scannerRef = useRef<QRScannerDialogRef>(null)
  const patientSearchSelectorRef = useRef<PatientSearchSelectorRef>(null)
  const [form, setForm] = useState({ patientId: '', itemIds: [] as string[] })

  const assignPatient = useAssignBed()
  const { data: patientItems, isFetching: isFetchingPatientItems } = useGetPatientItems(
    form.patientId,
    !!form.patientId,
  )

  const selectedPatient = form.patientId ? patientItems?.patient : undefined
  const items = form.patientId ? (patientItems?.items ?? []) : []

  const hasPatient = !!patient
  const isInvalid = !form.patientId || hasPatient

  const isPatientHasBed = !!selectedPatient?.bedId

  useEffect(() => {
    const nextItemIds = selectedPatient?.bedId && items.length ? items.map(item => item.itemId) : []

    setForm(prev => {
      const same = prev.itemIds.length === nextItemIds.length && prev.itemIds.every((id, i) => id === nextItemIds[i])

      if (same) return prev

      return {
        ...prev,
        itemIds: nextItemIds,
      }
    })
  }, [selectedPatient?.bedId, items])

  const patientItemOptions = useMemo<MultiSelectOption[]>(
    () =>
      items.map(item => {
        return {
          value: item.itemId,
          label: [item.productName, item.itemSerialNumber].filter(Boolean).join(' - ') || '-',
        }
      }),
    [items, t],
  )

  const handleScan = useCallback(
    (text: string) => {
      try {
        const url = new URL(text)
        const match = url.pathname.match(/\/scan\/patient\/([^/]+)/)
        const patientId = match?.[1]

        if (!patientId) {
          showError(t('common.qrScanner.invalidQr'))
          return
        }

        setForm(prev => ({
          ...prev,
          patientId,
          itemIds: [],
        }))
        patientSearchSelectorRef.current?.reset()
      } catch {
        showError(t('common.qrScanner.invalidQr'))
      }
    },
    [t],
  )

  async function handleAssignPatient() {
    if (isInvalid) return

    const options = getMutationOptions({
      successMessage: t('bed.toast.assignSuccess'),
      onSuccess: () => setForm({ patientId: '', itemIds: [] as string[] }),
    })

    const content =
      items.length > 0 ? (
        <div className="flex flex-col gap-2 text-sm">
          {form.itemIds.length > 0 ? (
            <>
              <span>
                {t('bed.detail.patientSection.equipmentWillMove', {
                  count: form.itemIds.length,
                  all: items.length,
                })}
              </span>

              <div className="flex flex-col gap-1">
                {items
                  .filter(item => form.itemIds.includes(item.itemId))
                  .map(item => (
                    <span key={item.itemId} className="text-xs text-muted-foreground">
                      {[item.productName, item.itemSerialNumber].filter(Boolean).join(' - ')}
                    </span>
                  ))}
              </div>
            </>
          ) : (
            <span className="text-sm text-destructive">{t('bed.detail.patientSection.noEquipmentWillMove')}</span>
          )}
        </div>
      ) : null

    if (isPatientHasBed) {
      const confirmed = await confirm({
        variant: 'destructive',
        title: `${t('bed.detail.patientSection.confirmChangeBedTitle')}`,
        description: `${t('bed.detail.patientSection.confirmChangeBedDescription', { name: selectedPatient?.fullName, fromBed: bed.name, toBed: selectedPatient.bedName })}`,
        content,
      })

      if (!confirmed) return
    }
    assignPatient.mutate({ departmentId: departmentId, id: bed.id, payload: form }, options)
  }

  async function handleDisChargePatient() {
    if (!hasPatient) return
    const confirmed = await confirm({
      variant: 'destructive',
      title: `${t('bed.detail.patientSection.confirmDischargeTitle')}`,
      description: `${t('bed.detail.patientSection.confirmDischargeDescription', { name: patient?.fullName })}`,
    })

    if (confirmed) {
      const options = getMutationOptions({
        successMessage: t('bed.toast.dischargeSuccess'),
      })

      const payload = {
        patientId: null,
      }
      assignPatient.mutate({ departmentId: departmentId, id: bed.id, payload }, options)
    }
  }
  return (
    <>
      <SectionCard
        title={t('bed.detail.managePatient')}
        cardClass="mt-4"
        headerClass="pb-0"
        right={
          <>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => scannerRef.current?.start()}
              disabled={isFetchingPatientItems}
              title={t('common.qrScanner.scanButton')}
            >
              <ScanLine className="size-4" />
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {hasPatient ? (
            <>
              <Button variant="destructive" onClick={handleDisChargePatient} className="text-xs" disabled={!hasPatient}>
                {t('common.discharge')}
              </Button>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <PatientSearchSelector
                    ref={patientSearchSelectorRef}
                    onSelect={patient => {
                      setForm(prev => ({
                        ...prev,
                        patientId: patient?.id ?? '',
                        itemIds: [],
                      }))
                    }}
                  ></PatientSearchSelector>

                  <PatientCard patient={selectedPatient} variant="selected" isLoading={false}></PatientCard>
                </div>

                {isPatientHasBed && (
                  <MultiSelect
                    options={patientItemOptions}
                    value={form.itemIds}
                    onValueChange={itemIds =>
                      setForm(prev => ({
                        ...prev,
                        itemIds,
                      }))
                    }
                    placeholder={t('common.placeholder.selectEquipment')}
                    searchPlaceholder={t('common.placeholder.searchEquipment')}
                    emptyText={t('common.placeholder.noEquipment')}
                  />
                )}
              </div>
              <Button onClick={handleAssignPatient} className="text-xs" disabled={isInvalid}>
                {t('common.add')}
              </Button>
            </>
          )}
        </div>
      </SectionCard>

      <QRScannerDialog ref={scannerRef} onScan={handleScan} />
    </>
  )
}
